import { Choice } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import { getVariantSavingValue } from '@thg-commerce/enterprise-pricing/src/getVariantSavingValue'

import { SelectedOptions } from '../../ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  FulfilmentMethod,
  InStockLocation,
  AvailabilitySource,
  ProductImage,
  RichContentItem,
  SubscriptionContract,
  SubscriptionFrequency,
} from '../../generated/graphql'
import { productGallery } from './gallery'
import { productDescription } from './description'
import { productContentSanitize } from './content'
import { ProductVariantItem } from '../../ApolloProvider/resolvers/Types/ProductVariant'
import { wasPrice as wasPriceTransformer } from './wasPrice'
import {
  SubscriptionChoice,
  SubscriptionData,
  IconsAvailability,
} from '../../ApolloProvider/resolvers/Types/Product'
import {
  MarketedSpecialOfferSummary,
  productMarketedSpecialOffer,
} from './marketedSpecialOffer'
import { pricePerUnit as pricePerUnitTransformer } from './pricePerUnit'
import { fulfilmentMethod } from './fulfilment'

export enum SubscriptionOptionsPaymentType {
  UPFRONT = 'Upfront',
  ON_DISPATCH = 'On_Dispatch',
  ON_ORDER = 'On_Order',
}

type CombinedChoice = Choice & SubscriptionChoice

export type Variant = {
  title: string
  images: ProductImage[]
  vipPrice?: {
    displayValue: string
    scalarValue: string
    amount: string
  } | null
  price?: {
    price: {
      displayValue: string
      scalarValue: string
      amount: string
    }
    rrp: {
      displayValue: string
      scalarValue: string
      amount: string
    }
  } | null
  returnsInformation: RichContentItem | null
  deliveryInformation: RichContentItem | null
  deliveryReturnsContentDefined: boolean
  isSubscription: boolean
  isAutoRenewSubscription: boolean
  subscriptionFrequency?: SubscriptionFrequency
  subscriptionTerm?: number
  subscriptionPaymentType?: SubscriptionOptionsPaymentType | null
  subscriptionContracts: SubscriptionContract[]
  marketedSpecialOffer: MarketedSpecialOfferSummary | null
  selectedOptions?: SelectedOptions | null
  choices: CombinedChoice[]
  eligibleForFulfilmentMethods?: FulfilmentMethod[]
  outOfGauge?: boolean
  storeDeliveryEnabled?: boolean
  leadTime?: number | null
  weightGroups?: string[] | null
  isBookable?: boolean
  inStockLocations?: InStockLocation[] | null
  isCheckStock?: boolean | null
  isNoCheckStock?: boolean
  isOrderInStore?: boolean
  icons?: {
    homeDelivery: boolean
    storeAvailable: boolean
    storeDelivery: boolean
  }
  product?: {
    url?: string | null
    brand?: {
      name: string | null
    }
    breadcrumbs?:
      | {
          pagePath: string
          displayName: string
          __typename?: string
        }[]
      | null
    content: {
      key: string
      value:
        | {
            stringListValue: string[]
          }
        | { richContentValue: { content: { content: string }[] } }
    }[]
  } | null
  category?: string | null
  powerReviewDescription?: string | null
  powerReviewTitle?: string | null
  deliveryStock?: boolean | null
  availabilitySource: AvailabilitySource
}

type Product = {
  sku: number
  linkedOn?: string | null
  iconsAvailability: IconsAvailability | null
}

type InputVariant = Variant & {
  product?: Product | null
}

type ProductWithVariants = Product & {
  defaultVariant?: Variant | null
  variants: InputVariant[]
}

export const defaultVariant = ({
  product,
  tags,
  config,
  descriptionKeys,
}: {
  product: ProductWithVariants
  tags: string[]
  config: {
    enablePreselectedProductVariant?: boolean
    productContentItemWasPriceTypeKey?: string
    showPowerReview?: boolean
  }
  descriptionKeys?: {
    items: string[]
    details: string[]
    video: string[]
    download: string[]
  }
}): (Variant | InputVariant) & {
  gallery: ReturnType<typeof productGallery>
  subscriptionData: SubscriptionData
  iconsAvailability: IconsAvailability | null
} => {
  let selectedOptions = {}
  const subscriptionOptions = {
    upfront: (product?.variants || []).reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.UPFRONT
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
    onDispatch: (product?.variants || []).reduce(
      (subscriptionChoices: SubscriptionChoice[], variant) => {
        if (
          variant.subscriptionPaymentType ===
          SubscriptionOptionsPaymentType.ON_DISPATCH
        ) {
          subscriptionChoices.push({
            ...variant.choices[0],
            price: variant.price?.price.displayValue,
            saving: variant.price ? getVariantSavingValue(variant.price) : null,
          })
        }
        return subscriptionChoices
      },
      [],
    ),
  }

  const category =
    (config.showPowerReview &&
      product?.defaultVariant?.product?.breadcrumbs
        ?.slice(1)
        .map((breadcrumb) => breadcrumb.displayName)
        .join('>')) ||
    ''

  const powerReviewDescription =
    product?.defaultVariant?.product && descriptionKeys
      ? productDescription(product.defaultVariant.product, descriptionKeys)
          ?.items[0]?.content ?? ''
      : ''

  const deliveryStock = product?.defaultVariant?.eligibleForFulfilmentMethods
    ? product.defaultVariant.eligibleForFulfilmentMethods?.includes(
        FulfilmentMethod.HomeDelivery,
      ) &&
      product.defaultVariant.inStockLocations?.includes(
        InStockLocation.Warehouse,
      )
    : product?.defaultVariant?.inStock

  if (product.defaultVariant) {
    if (product.defaultVariant.choices) {
      selectedOptions = config.enablePreselectedProductVariant
        ? product.defaultVariant.choices.reduce<SelectedOptions>(
            (accumulator, choice) => {
              accumulator[choice.optionKey] = choice.key

              return accumulator
            },
            {},
          )
        : product.defaultVariant.choices.reduce<SelectedOptions>(
            (accumulator, choice) => {
              if (choice.optionKey === product.linkedOn) {
                accumulator[choice.optionKey] = choice.key
              }
              return accumulator
            },
            {},
          )
    }

    const wasPrice = wasPriceTransformer(
      product.defaultVariant.product?.content,
      config.productContentItemWasPriceTypeKey,
    )

    const pricePerUnit = pricePerUnitTransformer(
      product.defaultVariant?.price?.price?.displayValue,
      product.defaultVariant.product?.content,
      product.defaultVariant?.price?.price?.amount,
    )

    const fulfilment = fulfilmentMethod({
      weightGroups: product?.defaultVariant?.weightGroups,
      inStockLocations: product?.defaultVariant?.inStockLocations,
      leadTime: product?.defaultVariant?.leadTime,
      isBookable: product?.defaultVariant?.isBookable,
      isCheckStock: product?.defaultVariant?.isCheckStock,
      isNoCheckStock: product?.defaultVariant?.isNoCheckStock,
      isOrderInStore: product?.defaultVariant?.isOrderInStore,
      eligibleForFulfilmentMethods:
        product?.defaultVariant?.eligibleForFulfilmentMethods,
      variants: product.variants,
    })

    return {
      ...product.defaultVariant,
      ...fulfilment,
      wasPrice,
      pricePerUnit,
      selectedOptions,
      deliveryStock,
      powerReviewTitle: productContentSanitize(product.defaultVariant.title),
      category: productContentSanitize(category),
      powerReviewDescription: productContentSanitize(powerReviewDescription),
      gallery: productGallery(product.defaultVariant, tags),
      price: product.defaultVariant.price?.price
        ? {
            ...product.defaultVariant.price,
            price:
              product.defaultVariant?.vipPrice ??
              product.defaultVariant.price?.price,
          }
        : null,
      vipPrice: product.defaultVariant?.vipPrice
        ? product.defaultVariant?.vipPrice
        : null,
      returnsInformation: product.defaultVariant.returnsInformation
        ?.content?.[0]?.content
        ? product.defaultVariant.returnsInformation.content[0].content
        : null,
      deliveryInformation: product.defaultVariant.deliveryInformation
        ?.content?.[0]?.content
        ? product.defaultVariant.deliveryInformation.content[0].content
        : null,
      deliveryReturnsContentDefined:
        product.defaultVariant.deliveryInformation?.content?.[0]?.content &&
        product.defaultVariant.returnsInformation?.content?.[0]?.content
          ? true
          : false,
      subscriptionData: {
        ...getSubscriptionData(product.defaultVariant),
        subscribeOptions: subscriptionOptions,
      },
      marketedSpecialOffer: product.defaultVariant?.marketedSpecialOffer
        ? productMarketedSpecialOffer(product.defaultVariant)
        : null,
      brand: {
        name: product.defaultVariant?.product?.brand?.name ?? '',
      },
      url: product.defaultVariant?.product?.url ?? null,
      iconsAvailability: product?.defaultVariant?.eligibleForFulfilmentMethods
        ? {
            homeDelivery: Boolean(
              product.defaultVariant.eligibleForFulfilmentMethods.includes(
                FulfilmentMethod.HomeDelivery,
              ),
            ),
            storeAvailable: Boolean(
              product.defaultVariant.eligibleForFulfilmentMethods.includes(
                FulfilmentMethod.CollectInStore,
              ),
            ),
            storeDelivery: Boolean(
              product.defaultVariant.eligibleForFulfilmentMethods.includes(
                FulfilmentMethod.DeliverToStore,
              ),
            ),
          }
        : null,
      availabilitySource: product.defaultVariant.availabilitySource
        ? product.defaultVariant.availabilitySource
        : AvailabilitySource.Warehouse,
    }
  }

  const masterSKU = product.sku
  const masterProductVariant = product.variants.find(
    (variant) => variant.product?.sku === masterSKU,
  )

  const defaultVariant = masterProductVariant || product.variants[0]

  if (defaultVariant && defaultVariant.choices) {
    selectedOptions = config.enablePreselectedProductVariant
      ? defaultVariant.choices.reduce<SelectedOptions>(
          (accumulator, choice) => {
            accumulator[choice.optionKey] = choice.key

            return accumulator
          },
          {},
        )
      : defaultVariant.choices.reduce<SelectedOptions>(
          (accumulator, choice) => {
            if (choice.optionKey === product.linkedOn) {
              accumulator[choice.optionKey] = choice.key
            }
            return accumulator
          },
          {},
        )
  }

  const wasPrice = wasPriceTransformer(
    defaultVariant.product?.content,
    config.productContentItemWasPriceTypeKey,
  )

  const pricePerUnit = pricePerUnitTransformer(
    defaultVariant?.price?.price?.displayValue,
    defaultVariant.product?.content,
    defaultVariant?.price?.price?.amount,
  )

  return {
    ...defaultVariant,
    wasPrice,
    selectedOptions,
    pricePerUnit,
    gallery: productGallery(defaultVariant, tags),
    returnsInformation: defaultVariant.returnsInformation?.content?.[0]?.content
      ? defaultVariant.returnsInformation.content[0].content
      : null,
    deliveryInformation: defaultVariant.deliveryInformation?.content?.[0]
      ?.content
      ? defaultVariant.deliveryInformation.content[0].content
      : null,
    deliveryReturnsContentDefined:
      defaultVariant.deliveryInformation?.content?.[0]?.content &&
      defaultVariant.returnsInformation?.content?.[0]?.content
        ? true
        : false,
    price: defaultVariant.price?.price
      ? {
          ...defaultVariant.price,
          price: defaultVariant?.vipPrice ?? defaultVariant.price?.price,
        }
      : null,
    vipPrice: defaultVariant?.vipPrice ?? null,
    subscriptionData: getSubscriptionData(defaultVariant),
    marketedSpecialOffer: defaultVariant?.marketedSpecialOffer
      ? productMarketedSpecialOffer(defaultVariant)
      : null,
    iconsAvailability: defaultVariant?.eligibleForFulfilmentMethods
      ? {
          homeDelivery: Boolean(
            defaultVariant.eligibleForFulfilmentMethods.includes(
              FulfilmentMethod.HomeDelivery,
            ),
          ),
          storeAvailable: Boolean(
            defaultVariant.eligibleForFulfilmentMethods.includes(
              FulfilmentMethod.CollectInStore,
            ),
          ),
          storeDelivery: Boolean(
            defaultVariant.eligibleForFulfilmentMethods.includes(
              FulfilmentMethod.DeliverToStore,
            ),
          ),
        }
      : null,
  }
}

const getSubscriptionData = (
  variant: Variant | InputVariant,
): SubscriptionData => {
  return {
    isSubscription: variant.isSubscription ?? false,
    isAutoRenewSubscription: variant.isAutoRenewSubscription ?? false,
    subscriptionFrequency: variant.subscriptionFrequency ?? null,
    subscriptionTerm: variant.subscriptionTerm ?? null,
    subscriptionPaymentType: variant.subscriptionPaymentType ?? null,
    subscriptionContracts: variant?.subscriptionContracts ?? [],
    choices:
      variant.isSubscription && variant.choices.length
        ? [
            {
              ...variant.choices[0],
              price: variant.price?.price.displayValue,
              saving: variant.price
                ? getVariantSavingValue(variant.price)
                : null,
            },
          ]
        : [],
  }
}

export type ProductWithDefaultVariant = {
  sku: number
  variants: ProductVariantItem[]
  defaultVariant?: ProductVariantItem
}

export const getDefaultVariantNew = (product?: ProductWithDefaultVariant) => {
  if (!product) {
    return null
  }

  if (product.defaultVariant) {
    return product.defaultVariant
  }

  const masterSKU = product.sku
  const masterProductVariant = product.variants.find(
    (variant) => variant.product?.sku === masterSKU,
  )
  const defaultVariant = masterProductVariant || product.variants[0]
  return defaultVariant
}
